import React from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2 } from 'lucide-react';
import companyAddFormViewModel from '../../../view-model/professional/company/company-add-form-view-model';
import { subscriptionViewModel } from '../../../view-model/professional/subscription-view-model';
import compagnyModel from '../../../model/professional/compagny-model';
import { HTTP_STATUS_CODE } from '../../../utils/UHttp';
import FormField from '../../ui/form-field';
import CompanyLogoUpload from '../../company/company-logo-upload';
import CompanyGalleryPhotos from '../../company/company-gallery-photos';
import toastStore from '../../../store/toast-store';

interface CompanyStepProps {
  onComplete: () => void;
  onBack: () => void;
  hideBackButton?: boolean;
}

const CompanyStep: React.FC<CompanyStepProps> = observer(({ onComplete, onBack, hideBackButton = false }) => {
  const { t, i18n } = useTranslation();
  const vm = companyAddFormViewModel;
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    vm.resetForm();
    return () => vm.cleanup();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Valider le formulaire
      if (!vm.validateForm(t)) {
        setIsSubmitting(false);
        return;
      }

      // Créer l'entreprise
      const submissionData = {
        ...vm.formData,
        galleryPhoto: vm.galleryPhotoFiles
      };

      const response: any = await compagnyModel.handleCreateCompany(submissionData);

      if (response.status === HTTP_STATUS_CODE.CREATED) {
        const companyId = response.company?.id;

        if (!companyId) {
          toastStore.error('Entreprise créée mais ID manquant');
          setIsSubmitting(false);
          return;
        }

        // Upload du logo si présent
        if (vm.formData.logo) {
          try {
            await compagnyModel.handleUpdateCompanyPicture(companyId, vm.formData.logo);
          } catch (logoError) {
            console.error("Error uploading logo:", logoError);
          }
        }

        if (vm.galleryPhotoFiles.length > 0) {
          try {
            await compagnyModel.handleUpdateGalleryPhotos(companyId, vm.galleryPhotoFiles);
          } catch (galleryError) {
            console.error("Error uploading gallery photos:", galleryError);
          }
        }

        // Si l'utilisateur a déjà un abonnement (hideBackButton = true), pas besoin de Stripe
        if (hideBackButton) {
          toastStore.success('Entreprise créée avec succès');
          onComplete();
          navigate(`/${i18n.language}/provider/dashboard`);
        } else {
          const selectedPlan = localStorage.getItem('onboarding_selected_plan');

          if (selectedPlan) {
            localStorage.setItem('stripe_return_url', `/${i18n.language}/provider/dashboard`);

            const success = await subscriptionViewModel.subscribe(selectedPlan as any);

            if (!success) {
              setIsSubmitting(false);
            }
          } else {
            toastStore.success('Entreprise créée avec succès');
            onComplete();
            navigate(`/${i18n.language}/provider/dashboard`);
          }
        }
      } else {
        toastStore.error(response.message || 'Erreur lors de la création de l\'entreprise');
        setIsSubmitting(false);
      }
    } catch (error: any) {
      console.error('Error creating company:', error);
      toastStore.error(error.message || 'Une erreur est survenue');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="py-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Créez votre entreprise
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Configurez votre première entreprise pour commencer
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations de base */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            id="businessName"
            name="businessName"
            label={t('company.form.businessName', 'Nom de l\'entreprise')}
            placeholder={String(t('company.form.businessNamePlaceholder', 'Ex: Salon de coiffure John Doe'))}
            defaultValue={vm.formData.businessName}
            onChange={vm.handleChange}
            required
            className={vm.errors.businessName ? 'error' : ''}
            help={vm.errors.businessName || undefined}
            success={vm.successMessages.businessName || undefined}
          />

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('company.form.siren', 'Numéro SIREN')}
              {vm.formData.sirenVerified && (
                <span className="inline-flex items-center">
                  <CheckCircle size={16} className="text-green-500" />
                  <span className="ml-1 text-xs text-green-500 font-medium">{t('company.form.verified', 'Vérifié')}</span>
                </span>
              )}
            </label>
            <div className="flex gap-2">
              <input
                id="siren"
                name="siren"
                type="text"
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                  vm.formData.sirenVerified
                    ? 'border-green-500 dark:border-green-700'
                    : vm.errors.siren
                      ? 'border-red-500 dark:border-red-700'
                      : 'border-gray-300 dark:border-gray-700'
                }`}
                placeholder="123456789"
                maxLength={9}
                value={vm.formData.siren}
                onChange={vm.handleChange}
                required
              />
              <button
                type="button"
                disabled={vm.isVerifyingSiren || !vm.formData.siren || vm.formData.siren.length !== 9 || vm.formData.sirenVerified}
                onClick={() => vm.verifySiren(t)}
                className={`px-3 py-2 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center min-w-[100px] ${
                  vm.isVerifyingSiren || !vm.formData.siren || vm.formData.siren.length !== 9 || vm.formData.sirenVerified
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-dark'
                }`}
              >
                {vm.isVerifyingSiren ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {t('common.verifying', 'Vérification...')}
                  </>
                ) : (
                  t('common.verify', 'Vérifier')
                )}
              </button>
            </div>
            {vm.errors.siren && (
              <p className="text-xs text-red-500 mt-1">{vm.errors.siren}</p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('company.form.sirenHelp', 'Numéro SIREN à 9 chiffres pour vérifier votre entreprise')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            id="phone"
            name="phone"
            label={String(t('company.form.address', 'Téléphone'))}
            placeholder="Ex: 003366666xxxxxx"
            defaultValue={vm.formData.phone}
            onChange={vm.handleChange}
            required
            className={vm.errors.phone ? 'error' : ''}
          />

          <FormField
            id="email"
            name="email"
            label={String(t('company.form.address', 'Email'))}
            placeholder="Ex: contact@exemple.com"
            defaultValue={vm.formData.email}
            onChange={vm.handleChange}
            required
            className={vm.errors.email ? 'error' : ''}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            id="domain"
            name="domain"
            label={String(t('company.form.domain', 'Domaine d\'activité'))}
            placeholder={String(t('company.form.domainPlaceholder', 'Ex: Esthétique, Coiffure, Massage...'))}
            defaultValue={vm.formData.domain}
            onChange={vm.handleChange}
            required
            className={vm.errors.domain ? 'error' : ''}
            help={vm.errors.domain || String(t('company.form.domainHelp', 'Le secteur d\'activité de votre entreprise'))}
          />

          <FormField
            id="description"
            name="description"
            label={String(t('company.form.description', 'Description'))}
            type="textarea"
            placeholder={String(t('company.form.descriptionPlaceholder', 'Décrivez votre entreprise...'))}
            defaultValue={vm.formData.description}
            onChange={vm.handleChange}
            help={String(t('company.form.descriptionHelp', 'Cette description sera visible sur votre profil public'))}
          />
        </div>

        {/* Adresse */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            id="address"
            name="address"
            label={String(t('company.form.address', 'Adresse'))}
            placeholder={String(t('company.form.addressPlaceholder', 'Ex: 123 Rue de Paris'))}
            defaultValue={vm.formData.address}
            onChange={vm.handleChange}
            required
            className={vm.errors.address ? 'error' : ''}
          />

          <FormField
            id="postalCode"
            name="postalCode"
            label={String(t('company.form.postalCode', 'Code postal'))}
            placeholder="75001"
            defaultValue={vm.formData.postalCode}
            onChange={vm.handleChange}
            required
            className={vm.errors.postalCode ? 'error' : ''}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            id="city"
            name="city"
            label={String(t('company.form.city', 'Ville'))}
            placeholder={String(t('company.form.cityPlaceholder', 'Ex: Paris'))}
            defaultValue={vm.formData.city}
            onChange={vm.handleChange}
            required
            className={vm.errors.city ? 'error' : ''}
          />

          <FormField
            id="country"
            name="country"
            label={String(t('company.form.country', 'Pays'))}
            placeholder={String(t('company.form.countryPlaceholder', 'Ex: France'))}
            defaultValue={vm.formData.country}
            onChange={vm.handleChange}
            required
            className={vm.errors.country ? 'error' : ''}
          />
        </div>

        {/* Conditions de réservation */}
        <FormField
          id="reservationConditions"
          name="reservationConditions"
          label={String(t('company.form.reservationConditions', 'Conditions de réservation'))}
          type="textarea"
          placeholder={String(t('company.form.reservationConditionsPlaceholder', 'Ex: Annulation gratuite jusqu\'à 24h avant...'))}
          defaultValue={vm.formData.reservationConditions}
          onChange={vm.handleChange}
          help={String(t('company.form.reservationConditionsHelp', 'Précisez vos conditions d\'annulation, de remboursement, etc.'))}
        />

        {/* Méthodes de paiement */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('company.form.paymentMethods', 'Méthodes de paiement acceptées')}
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {['Cash', 'Credit Card', 'Bank Transfer', 'Check', 'PayPal', 'Mobile Payment'].map((method) => (
              <div key={method} className="flex items-center">
                <input
                  type="checkbox"
                  id={`payment-${method}`}
                  checked={vm.formData.paymentMethods.includes(method)}
                  onChange={(e) => vm.handlePaymentMethodChange(method, e.target.checked)}
                  className="h-4 w-4 text-primary border-gray-300 dark:border-gray-600 rounded focus:ring-primary dark:bg-gray-700"
                />
                <label htmlFor={`payment-${method}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t(`company.form.paymentMethod.${method.toLowerCase().replace(' ', '')}`, method)}
                </label>
              </div>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('company.form.paymentMethodsHelp', 'Sélectionnez les méthodes de paiement que vous acceptez')}
          </p>
        </div>

        {/* Logo */}
        <CompanyLogoUpload viewModel={vm} />

        {/* Galerie de photos */}
        <CompanyGalleryPhotos viewModel={vm} />

        {/* Buttons */}
        <div className={`flex items-center pt-6 border-t border-gray-200 dark:border-gray-700 ${hideBackButton ? 'justify-end' : 'justify-between'}`}>
          {!hideBackButton && (
            <button
              type="button"
              onClick={onBack}
              disabled={isSubmitting}
              className="px-6 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Retour
            </button>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-8 py-3 rounded-lg font-semibold transition-all ${
              !isSubmitting
                ? 'bg-gradient-to-r from-primary to-primary-light hover:from-primary-dark hover:to-primary text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Création en cours...
              </span>
            ) : (
              'Créer mon entreprise'
            )}
          </button>
        </div>
      </form>
    </div>
  );
});

export default CompanyStep;
